import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, TOKEN_KEY } from "@/lib/api";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setUser(null); return; }
    try {
      setUser(await api("/auth/me", { token }));
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    }
  }, []);

  useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  const applySession = useCallback(async (data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    qc.clear();
    setUser(data.user);
    return data.user;
  }, [qc]);

  const value = useMemo(() => ({
    user,
    loading,
    refresh,
    login: async (email, password) => applySession(await api("/auth/login", { method: "POST", body: { email, password }, token: null })),
    register: async (name, email, password, role, prefs) =>
      applySession(await api("/auth/register", { method: "POST", body: { name, email, password, role, ...(prefs ?? {}) }, token: null })),
    setUser,
    logout: async () => {
      localStorage.removeItem(TOKEN_KEY);
      qc.clear();
      setUser(null);
    },
  }), [user, loading, refresh, applySession, qc]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

export function homeForRole(role) {
  if (role === "trainer") return "/trainer";
  if (role === "admin") return "/admin";
  if (role === "member") return "/discover";
  return "/";
}
